import { FormControl, FormLabel, Spinner, Text } from '@chakra-ui/react';
import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconChevronTopSmall } from 'central-icons/IconChevronTopSmall';
import classNames from 'classnames';
import { type UseComboboxProps, useCombobox } from 'downshift';
import type React from 'react';

import { Input } from '@revelio/ui';

import styles from './Autocomplete.module.css';
import layoutStyles from './autocomplete.layout.module.css';

export interface AutocompleteProps<T> {
  inputId: string;
  dataTestId?: string;
  placeholder?: string;
  formLabel?: string;
  inputValue: string;
  setInputValue: React.Dispatch<string>;
  suggestions: T[];
  dropdownHeader?: string;
  initialFocusRef?: React.Ref<HTMLInputElement>;
  showRightAddon?: boolean;
  showLoader?: boolean;
  isLoading?: boolean;
  isTypeaheadDisabled?: boolean;
  comboBoxProps?: UseComboboxProps<T>;
}

export function Autocomplete<T>({
  inputId,
  dataTestId,
  placeholder,
  formLabel,
  inputValue,
  setInputValue,
  suggestions,
  dropdownHeader,
  initialFocusRef,
  showRightAddon,
  showLoader,
  isLoading,
  isTypeaheadDisabled,
  comboBoxProps,
}: AutocompleteProps<T>) {
  const {
    isOpen,
    getLabelProps,
    getComboboxProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useCombobox(
    comboBoxProps || {
      items: suggestions,
      inputValue,
      onInputValueChange: ({ inputValue: newValue, type }) => {
        // Manual typing - use the event to preserve cursor position
        if (type === useCombobox.stateChangeTypes.InputChange) {
          return;
        }

        // Selection from dropdown - update directly
        setInputValue(newValue || '');
      },
      itemToString: (item: unknown) =>
        (item as { label?: string; name?: string })?.label ||
        (item as { label?: string; name?: string })?.name ||
        '',
    }
  );

  const inputProps = getInputProps({
    onKeyDown: (e) => {
      const isSuggestionHighlighted = highlightedIndex > -1;
      if (isSuggestionHighlighted) {
        e.stopPropagation();
      }
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    refKey: 'ref',
    ref: initialFocusRef,
  });

  const showSpinner = !isTypeaheadDisabled && showLoader && isLoading;
  const showToggle = !isTypeaheadDisabled && showRightAddon;

  return (
    <div>
      <FormControl isRequired>
        {formLabel && (
          <FormLabel {...getLabelProps()} fontSize="sm">
            {formLabel}
          </FormLabel>
        )}
        <div {...getComboboxProps()}>
          <Input
            size="sm"
            id={inputId}
            data-testid={dataTestId}
            placeholder={placeholder}
            type="text"
            // Mirrors the FormControl isRequired above: the ui Input does not
            // read Chakra's form context.
            required
            className={styles.input}
            wrapperClassName={styles.field}
            {...inputProps}
            endSlot={
              (showSpinner || showToggle) && (
                <>
                  {showSpinner && <Spinner color="green.500" size="xs" />}
                  {showToggle && (
                    <span
                      {...getToggleButtonProps()}
                      className={layoutStyles.toggle}
                    >
                      {isOpen ? (
                        <IconChevronDownSmall size={16} />
                      ) : (
                        <IconChevronTopSmall size={16} />
                      )}
                    </span>
                  )}
                </>
              )
            }
          />
        </div>
      </FormControl>

      {!isTypeaheadDisabled && (
        <div
          {...getMenuProps()}
          className={styles.menu}
          data-expanded={(isOpen && suggestions.length > 0) || undefined}
        >
          {isOpen && (
            <div className={styles.scrollBox}>
              {suggestions.length > 0 && (
                <div className={layoutStyles.dropdownHeader}>
                  <Text fontSize="xs" fontWeight="600">
                    {dropdownHeader}
                  </Text>
                </div>
              )}

              {suggestions.map((item, index) => (
                <div
                  key={index}
                  {...getItemProps({ item, index })}
                  className={classNames(
                    styles.item,
                    highlightedIndex === index && styles.itemHighlighted
                  )}
                >
                  <Text fontSize="xs" fontWeight="semi-bold">
                    {(item as { label?: string; name?: string }).label ||
                      (item as { label?: string; name?: string }).name}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
